import { useState, useMemo } from 'react'
import { useParams, Link } from 'react-router-dom'
import { Activity, Briefcase, TrendingUp, DollarSign, Target, PieChart, ShieldAlert, BarChart2 } from 'lucide-react'
import { useActuals } from '@/hooks/useActuals'
import { useSimulations } from '@/hooks/useSimulations'
import { useCapital } from '@/hooks/useCapital'
import { calculateHealthScore } from '@/lib/calculations/dashboardMetrics'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, BarChart, Bar } from 'recharts'

const formatIDR = (amount: number) => {
  return new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', maximumFractionDigits: 0 }).format(amount)
}

export function DashboardPage() {
  const { businessId } = useParams<{ businessId: string }>()
  const { actuals, isLoading: actualsLoading } = useActuals(businessId)
  const { simulations, isLoading: simLoading } = useSimulations(businessId)
  const { data: capitalData, isLoading: capLoading } = useCapital(businessId)
  const capitalSettings = capitalData?.settings || null

  const [activeTab, setActiveTab] = useState('executive')
  const [isAdvancedMode, setIsAdvancedMode] = useState(false)

  // Use actuals if available, otherwise simulations, otherwise empty
  const monthsData = useMemo(() => {
    if (actuals.length > 0) return [...actuals].reverse() // Ascending order
    return []
  }, [actuals])

  const health = useMemo(() => {
    return calculateHealthScore(monthsData, capitalSettings)
  }, [monthsData, capitalSettings])

  if (actualsLoading || simLoading || capLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-slate-500">
        <div className="w-8 h-8 border-4 border-brand-500 border-t-transparent rounded-full animate-spin mb-4" />
        <p>Menghitung data Dashboard...</p>
      </div>
    )
  }

  const latestData = monthsData.length > 0 ? monthsData[monthsData.length - 1] : null
  const sourceLabel = actuals.length > 0 ? 'Actual Data' : 'No Data'

  const tabs = [
    { id: 'executive', label: 'Executive', icon: Activity },
    { id: 'financial', label: 'Financial', icon: TrendingUp },
    { id: 'cashflow', label: 'Cash Flow', icon: DollarSign },
    ...(isAdvancedMode ? [{ id: 'account', label: 'Account Position', icon: Briefcase }] : []),
    { id: 'bep', label: 'BEP & Unit', icon: Target },
    { id: 'investment', label: 'Investment', icon: PieChart },
    ...(isAdvancedMode ? [{ id: 'operational', label: 'Operational', icon: BarChart2 }] : []),
    { id: 'founder', label: 'Founder', icon: ShieldAlert },
  ]

  return (
    <div className="space-y-6 pb-20">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-100">Business Dashboard</h1>
          <p className="text-sm text-slate-400 mt-1">Diagnosis dan pemantauan kinerja bisnis secara komprehensif.</p>
        </div>
        <div className="flex items-center gap-3 bg-slate-900 border border-slate-800 py-1.5 px-3 rounded-lg shadow-sm">
          <span className={`text-xs font-medium ${!isAdvancedMode ? 'text-brand-400' : 'text-slate-500'}`}>Basic</span>
          <button
            type="button"
            role="switch"
            aria-checked={isAdvancedMode}
            onClick={() => setIsAdvancedMode(!isAdvancedMode)}
            className={`${isAdvancedMode ? 'bg-brand-500' : 'bg-slate-700'} relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none`}
          >
            <span
              aria-hidden="true"
              className={`${isAdvancedMode ? 'translate-x-4' : 'translate-x-0'} pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
            />
          </button>
          <span className={`text-xs font-medium ${isAdvancedMode ? 'text-brand-400' : 'text-slate-500'}`}>Advanced</span>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex overflow-x-auto hide-scrollbar gap-2 pb-2">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors border
              ${activeTab === tab.id 
                ? 'bg-brand-500/10 text-brand-400 border-brand-500/20' 
                : 'bg-slate-900 text-slate-400 border-slate-800 hover:bg-slate-800 hover:text-slate-300'
              }`}
          >
            <tab.icon size={16} />
            {tab.label}
          </button>
        ))}
      </div>

      <div className="flex justify-end">
        <span className="text-[10px] font-bold px-2 py-0.5 rounded uppercase tracking-wider bg-slate-800 text-slate-300 border border-slate-700">
          Source: {sourceLabel}
        </span>
      </div>

      {/* Content */}
      {!latestData ? (
        <div className="bg-slate-900 border border-slate-800 p-10 rounded-xl text-center">
          <p className="text-slate-400 mb-4">Belum ada data aktual yang dicatat. Silakan catat performa bulan ini terlebih dahulu.</p>
          <Link to={`/businesses/${businessId}/actuals`} className="px-4 py-2 bg-brand-500 text-white rounded-lg font-medium hover:bg-brand-600 transition-colors">
            Input Actual Report
          </Link>
        </div>
      ) : (
        <>
          {activeTab === 'executive' && (
            <div className="space-y-6">
              {/* Executive Diagnosis */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-slate-900 border border-slate-800 p-6 rounded-xl flex flex-col justify-between shadow-sm">
                  <div>
                    <p className="text-sm text-slate-400 mb-1">Business Health Score</p>
                    <div className="flex items-end gap-3">
                      <span className="text-4xl font-bold text-slate-100">{health.score}</span>
                      <span className="text-lg text-slate-400 mb-1">/ 100</span>
                    </div>
                    <div className="w-full bg-slate-800 rounded-full h-2 mt-4 overflow-hidden">
                      <div className={`h-2 rounded-full ${health.score > 60 ? 'bg-emerald-500' : health.score > 40 ? 'bg-amber-500' : 'bg-rose-500'}`} style={{ width: `${health.score}%` }}></div>
                    </div>
                  </div>
                  <div className="mt-4">
                    <span className={`inline-block px-2 py-1 rounded text-xs font-bold uppercase tracking-wider
                      ${health.status === 'Strong' || health.status === 'Healthy' ? 'bg-emerald-500/10 text-emerald-400' : 
                        health.status === 'Warning' ? 'bg-amber-500/10 text-amber-400' : 'bg-rose-500/10 text-rose-400'}`}>
                      {health.status}
                    </span>
                  </div>
                </div>

                <div className={`border p-6 rounded-xl flex flex-col justify-center shadow-sm
                  ${health.decisionSignal === 'continue' ? 'bg-emerald-500/5 border-emerald-500/20' :
                    health.decisionSignal === 'close' ? 'bg-rose-500/5 border-rose-500/20' : 'bg-amber-500/5 border-amber-500/20'
                  }`}>
                  <p className="text-sm font-semibold mb-2 text-slate-400 uppercase tracking-wider">AI Diagnosis & Decision</p>
                  <p className="text-xl font-bold text-slate-100 uppercase tracking-wide mb-2">{health.decisionSignal.replace('_', ' ')}</p>
                  <p className="text-slate-300 text-sm leading-relaxed">{health.interpretation}</p>
                </div>
              </div>

              {/* Key Metrics */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <MetricCard title="Kas Saat Ini" value={latestData.cashBalance} />
                <MetricCard title="Pendapatan Bulan Ini" value={latestData.revenue} color="text-emerald-400" />
                <MetricCard title="Laba Operasional" value={latestData.revenue - latestData.cogs - latestData.operatingExpense} />
                <MetricCard title="Sisa Affordable Loss" value={(capitalSettings?.stopLossEquity || 0)} color="text-brand-400" />
              </div>
            </div>
          )}

          {activeTab === 'financial' && (
            <div className="space-y-6">
              <div className="bg-slate-900 border border-slate-800 p-6 rounded-xl shadow-sm">
                <h3 className="text-lg font-bold text-slate-200 mb-4">P&L (Profit & Loss) Summary</h3>
                <div className="space-y-3 font-mono text-sm">
                  <div className="flex justify-between text-emerald-400 pb-2 border-b border-slate-800">
                    <span>Revenue</span>
                    <span>{formatIDR(latestData.revenue)}</span>
                  </div>
                  <div className="flex justify-between text-rose-400 pb-2 border-b border-slate-800">
                    <span>- COGS</span>
                    <span>{formatIDR(latestData.cogs)}</span>
                  </div>
                  <div className="flex justify-between font-bold text-slate-200 pb-2 border-b border-slate-800">
                    <span>= Gross Profit</span>
                    <span>{formatIDR(latestData.revenue - latestData.cogs)}</span>
                  </div>
                  <div className="flex justify-between text-rose-400 pb-2 border-b border-slate-800">
                    <span>- Operating Expense</span>
                    <span>{formatIDR(latestData.operatingExpense)}</span>
                  </div>
                  <div className="flex justify-between font-bold text-slate-200 pb-2 border-b border-slate-800">
                    <span>= Operating Profit</span>
                    <span>{formatIDR(latestData.revenue - latestData.cogs - latestData.operatingExpense)}</span>
                  </div>
                  <div className="flex justify-between text-rose-400 pb-2 border-b border-slate-800">
                    <span>- Tax & Interest & Other</span>
                    <span>{formatIDR(latestData.tax + latestData.interest + latestData.otherExpense)}</span>
                  </div>
                  <div className={`flex justify-between font-bold pb-2 border-b border-slate-800 text-lg
                    ${(latestData.revenue - latestData.cogs - latestData.operatingExpense - latestData.tax - latestData.interest - latestData.otherExpense) >= 0 ? 'text-emerald-400' : 'text-rose-500'}`}>
                    <span>= Net Profit</span>
                    <span>{formatIDR(latestData.revenue - latestData.cogs - latestData.operatingExpense - latestData.tax - latestData.interest - latestData.otherExpense)}</span>
                  </div>
                </div>
              </div>

              {/* Chart */}
              <div className="bg-slate-900 border border-slate-800 p-6 rounded-xl shadow-sm h-80">
                <h3 className="text-md font-semibold text-slate-300 mb-4">Tren Revenue & Profit</h3>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={monthsData.map(m => ({
                    name: m.periodMonth, 
                    Revenue: m.revenue, 
                    Profit: m.revenue - m.cogs - m.operatingExpense - m.tax - m.interest - m.otherExpense
                  }))} margin={{ top: 5, right: 20, left: 20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                    <XAxis dataKey="name" stroke="#64748b" fontSize={12} tickLine={false} axisLine={false} />
                    <YAxis stroke="#64748b" fontSize={12} tickLine={false} axisLine={false} tickFormatter={(value) => `Rp${(value/1000000).toFixed(0)}M`} />
                    <Tooltip contentStyle={{ backgroundColor: '#0f172a', borderColor: '#1e293b', borderRadius: '8px' }} />
                    <Legend />
                    <Line type="monotone" dataKey="Revenue" stroke="#10b981" strokeWidth={2} dot={{ r: 4 }} />
                    <Line type="monotone" dataKey="Profit" stroke="#3b82f6" strokeWidth={2} dot={{ r: 4 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {activeTab === 'cashflow' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
               <div className="bg-slate-900 border border-slate-800 p-6 rounded-xl shadow-sm">
                  <h3 className="text-md font-semibold text-slate-300 mb-4">Posisi Kas Akhir</h3>
                  <p className="text-4xl font-bold text-blue-400 mb-2">{formatIDR(latestData.cashBalance)}</p>
                  <p className="text-sm text-slate-400">Total likuiditas di kas dan bank perusahaan saat ini.</p>
               </div>
               <div className="bg-slate-900 border border-slate-800 p-6 rounded-xl shadow-sm">
                  <h3 className="text-md font-semibold text-slate-300 mb-4">Cash Injection / Withdrawal</h3>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center pb-2 border-b border-slate-800">
                      <span className="text-sm text-slate-400">Penambahan Modal</span>
                      <span className="font-semibold text-emerald-400">{formatIDR(latestData.additionalCapital)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-slate-400">Penarikan Owner</span>
                      <span className="font-semibold text-rose-400">{formatIDR(latestData.ownerWithdrawal)}</span>
                    </div>
                  </div>
               </div>
            </div>
          )}

          {isAdvancedMode && activeTab === 'account' && (
            <div className="bg-slate-900 border border-slate-800 p-6 rounded-xl shadow-sm">
              <h3 className="text-lg font-bold text-slate-200 mb-4">Account Position (Balance Sheet Snapshot)</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <h4 className="text-sm font-semibold text-blue-400 uppercase tracking-wider mb-3">Aset</h4>
                  <ul className="space-y-2 text-sm text-slate-300">
                    <li className="flex justify-between"><span>Kas:</span> <span>{formatIDR(latestData.cashBalance)}</span></li>
                    <li className="flex justify-between"><span>Inventory:</span> <span>{formatIDR(latestData.inventoryValue)}</span></li>
                    <li className="flex justify-between"><span>Piutang:</span> <span>{formatIDR(latestData.receivables)}</span></li>
                    <li className="flex justify-between pt-2 border-t border-slate-800 font-bold">
                      <span>Total:</span> <span>{formatIDR(latestData.cashBalance + latestData.inventoryValue + latestData.receivables)}</span>
                    </li>
                  </ul>
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-rose-400 uppercase tracking-wider mb-3">Liabilitas</h4>
                  <ul className="space-y-2 text-sm text-slate-300">
                    <li className="flex justify-between"><span>Utang Usaha:</span> <span>{formatIDR(latestData.payables)}</span></li>
                    <li className="flex justify-between pt-2 border-t border-slate-800 font-bold">
                      <span>Total:</span> <span>{formatIDR(latestData.payables)}</span>
                    </li>
                  </ul>
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-purple-400 uppercase tracking-wider mb-3">Ekuitas</h4>
                  <ul className="space-y-2 text-sm text-slate-300">
                    <li className="flex justify-between"><span>Total Ekuitas Pendekatan:</span> </li>
                    <li className="flex justify-between font-bold"><span>{formatIDR((latestData.cashBalance + latestData.inventoryValue + latestData.receivables) - latestData.payables)}</span></li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'bep' && (
            <div className="bg-slate-900 border border-slate-800 p-6 rounded-xl shadow-sm text-center py-20">
              <Target size={48} className="mx-auto text-brand-400 mb-4 opacity-50" />
              <h2 className="text-xl font-bold text-slate-200">BEP & Unit Economics</h2>
              <p className="text-slate-400 mt-2">Segera hadir. Menampilkan target penjualan harian dan rasio margin kontribusi untuk mencapai titik impas.</p>
            </div>
          )}

          {activeTab === 'investment' && (
            <div className="bg-slate-900 border border-slate-800 p-6 rounded-xl shadow-sm">
              <h3 className="text-lg font-bold text-slate-200 mb-4">Investment & Capital Safety</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <MetricCard title="Batas Stop Loss (Affordable Loss)" value={capitalSettings?.stopLossEquity || 0} color="text-rose-400" />
                <MetricCard title="Initial Capital Total" value={capitalSettings?.initialCapital || 0} />
              </div>
            </div>
          )}

          {isAdvancedMode && activeTab === 'operational' && (
            <div className="bg-slate-900 border border-slate-800 p-6 rounded-xl shadow-sm text-center py-20">
              <BarChart2 size={48} className="mx-auto text-blue-400 mb-4 opacity-50" />
              <h2 className="text-xl font-bold text-slate-200">Operational Metrics</h2>
              <p className="text-slate-400 mt-2">Dapat dikustomisasi di masa mendatang untuk melacak Volume Penjualan, Average Order Value, dll.</p>
            </div>
          )}

          {activeTab === 'founder' && (
            <div className="bg-slate-900 border border-slate-800 p-6 rounded-xl shadow-sm text-center py-20">
              <ShieldAlert size={48} className="mx-auto text-purple-400 mb-4 opacity-50" />
              <h2 className="text-xl font-bold text-slate-200">Founder & Investor Ownership</h2>
              <p className="text-slate-400 mt-2">Detail persentase kepemilikan dan hak dividen dapat dilihat di modul Capital Management.</p>
            </div>
          )}
        </>
      )}
    </div>
  )
}

function MetricCard({ title, value, color = 'text-slate-100' }: { title: string, value: number, color?: string }) {
  return (
    <div className="bg-slate-900 border border-slate-800 p-4 rounded-xl shadow-sm">
      <p className="text-xs text-slate-400 mb-1">{title}</p>
      <p className={`text-lg md:text-xl font-bold ${color}`}>{formatIDR(value)}</p>
    </div>
  )
}
